import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, KeyRound, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import API_URL from '../api';

function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const bg = darkMode ? '#0f0f1a' : '#f0f2f5';
  const card = darkMode ? '#1a1a2e' : '#fff';
  const border = darkMode ? 'rgba(255,255,255,0.08)' : '#e0e0e0';
  const text = darkMode ? '#e0e0e0' : '#333';
  const subtext = darkMode ? '#888' : '#666';
  const inputBg = darkMode ? '#0f0f1a' : '#f8f8f8';

  const handleReset = async () => {
    if (password !== confirm) return setError('Passwords do not match!');
    if (password.length < 6) return setError('Password must be at least 6 characters!');
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/reset-password/${token}`, { password });
      setMsg(res.data.message);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Reset failed!');
    }
    setLoading(false);
  };

  const inputStyle = {
    background: inputBg,
    border: `1.5px solid ${border}`,
    color: text,
    padding: '12px 12px 12px 42px',
    borderRadius: '10px',
    fontSize: '14px',
    width: '100%',
    marginBottom: 0,
    outline: 'none',
    fontFamily: 'inherit'
  };

  return (
    <div style={{ minHeight: '100vh', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      <div style={{ width: '100%', maxWidth: '420px', animation: 'fadeInUp 0.5s ease' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', borderRadius: '16px', width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px', fontSize: '28px', boxShadow: '0 8px 25px rgba(233,69,96,0.3)' }}>
            🔑
          </div>
          <h1 style={{ color: text, fontSize: '26px', fontWeight: '700', marginBottom: '6px' }}>Reset Password</h1>
          <p style={{ color: subtext, fontSize: '14px' }}>Enter your new password below</p>
        </div>

        {/* Card */}
        <div style={{ background: card, borderRadius: '20px', padding: '35px', border: `1px solid ${border}`, boxShadow: darkMode ? '0 20px 60px rgba(0,0,0,0.4)' : '0 20px 60px rgba(0,0,0,0.08)' }}>

          {msg && (
            <div style={{ background: darkMode ? 'rgba(76,175,80,0.15)' : '#e8f5e9', border: '1px solid rgba(76,175,80,0.3)', borderRadius: '10px', padding: '12px 15px', marginBottom: '20px', color: '#4caf50', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={16} /> {msg} Redirecting...
            </div>
          )}

          {error && (
            <div style={{ background: darkMode ? 'rgba(233,69,96,0.15)' : '#ffebee', border: '1px solid rgba(233,69,96,0.3)', borderRadius: '10px', padding: '12px 15px', marginBottom: '20px', color: '#e94560', fontSize: '14px' }}>
              ❌ {error}
            </div>
          )}

          <div style={{ marginBottom: '14px' }}>
            <label style={{ color: subtext, fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>New Password</label>
            <div style={{ position: 'relative' }}>
              <Lock size={16} color="#888" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
              <input placeholder="••••••••" type="password" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ color: subtext, fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>Confirm Password</label>
            <div style={{ position: 'relative' }}>
              <KeyRound size={16} color="#888" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
              <input placeholder="••••••••" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleReset()} style={inputStyle} />
            </div>
          </div>

          <button
            onClick={handleReset}
            disabled={loading}
            style={{ background: 'linear-gradient(135deg, #e94560, #c62a47)', color: '#fff', width: '100%', padding: '14px', fontSize: '15px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 4px 15px rgba(233,69,96,0.3)' }}>
            {loading ? '⏳ Resetting...' : <><KeyRound size={17} /> Reset Password</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;